export type AppRoute =
  | { kind: "root" }
  | { kind: "newServer"; mode: "choose" | "do" | "byo" }
  | { kind: "settings"; section: SettingsSection }
  | { kind: "vps"; vpsId: string }
  | { kind: "vpsSettings"; vpsId: string }
  | { kind: "session"; vpsId: string; sessionId: string };

export type SettingsSection = "plan" | "account" | "telegram" | "appearance" | "security";

const RESERVED_TOP_LEVEL_SEGMENTS = new Set(["new-server", "settings", "tg", "auth"]);
const VPS_ID_RE = /^vps-[A-Za-z0-9_-]+$/;
const SESSION_ID_RE = /^ses-[A-Za-z0-9_-]+$/;
const SETTINGS_SECTIONS = new Set<SettingsSection>(["plan", "account", "telegram", "appearance", "security"]);

export function isVpsRouteId(value: string): boolean {
  return VPS_ID_RE.test(value);
}

export function isSessionRouteId(value: string): boolean {
  return SESSION_ID_RE.test(value);
}

export function parseAppRoute(pathname: string): AppRoute {
  const segments = pathname
    .split("/")
    .filter(Boolean)
    .map((segment) => decodeURIComponent(segment));

  if (segments.length === 0) {
    return { kind: "root" };
  }

  if (segments[0] === "settings") {
    if (segments.length === 1) {
      return { kind: "settings", section: "plan" };
    }
    if (segments.length === 2 && SETTINGS_SECTIONS.has(segments[1] as SettingsSection)) {
      return { kind: "settings", section: segments[1] as SettingsSection };
    }
    return { kind: "root" };
  }

  if (segments[0] === "new-server") {
    if (segments.length === 1) {
      return { kind: "newServer", mode: "choose" };
    }
    if (segments.length === 2 && (segments[1] === "do" || segments[1] === "byo")) {
      return { kind: "newServer", mode: segments[1] };
    }
    return { kind: "root" };
  }

  if (RESERVED_TOP_LEVEL_SEGMENTS.has(segments[0])) {
    return { kind: "root" };
  }

  if (!isVpsRouteId(segments[0])) {
    return { kind: "root" };
  }

  if (segments.length === 1) {
    return { kind: "vps", vpsId: segments[0] };
  }

  if (segments.length === 2 && segments[1] === "settings") {
    return { kind: "vpsSettings", vpsId: segments[0] };
  }

  if (segments.length === 2 && isSessionRouteId(segments[1])) {
    return { kind: "session", vpsId: segments[0], sessionId: segments[1] };
  }

  return { kind: "root" };
}

export function formatAppRoute(route: AppRoute): string {
  switch (route.kind) {
    case "root":
      return "/";
    case "newServer":
      return route.mode === "choose" ? "/new-server" : `/new-server/${route.mode}`;
    case "settings":
      return `/settings/${route.section}`;
    case "vps":
      return `/${encodeURIComponent(route.vpsId)}`;
    case "vpsSettings":
      return `/${encodeURIComponent(route.vpsId)}/settings`;
    case "session":
      return `/${encodeURIComponent(route.vpsId)}/${encodeURIComponent(route.sessionId)}`;
  }
}

export function foregroundRoute(vpsId: string | null, sessionId: string | null): AppRoute {
  if (vpsId && sessionId) {
    return { kind: "session", vpsId, sessionId };
  }
  if (vpsId) {
    return { kind: "vps", vpsId };
  }
  return { kind: "root" };
}

export function routeKey(route: AppRoute): string {
  return formatAppRoute(route);
}

export function isSettingsRoute(route: AppRoute): route is Extract<AppRoute, { kind: "settings" | "vpsSettings" }> {
  return route.kind === "settings" || route.kind === "vpsSettings";
}
